import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'neongrid',
  family: 'grid',
  energyLevel: 0.60,
  bpmMin: 88,
  bpmMax: 145,
  movement: 0.70,
  danceability: 0.70,
  tonality: 0.65,
  enabled: true
};

// NeonGrid: a full-screen grid of glowing dots whose per-column height
// rises and falls like a digital equalizer, with a diagonal shimmer and
// slow color drift layered on top so it never reads as a flat, literal
// spectrum display - "abstract... pulsating and shifting... rhythmic
// visual flow" rather than a real audio meter. The bar-height wave itself
// stays pure time-based motion (not literally beat-synced); only overall
// brightness gets a beat-locked accent on top - see BEAT_PULSE_* below
// and beat-pulse.js.
//
// Distinct from SpotWall (this pilot's other dot-grid visualizer):
// SpotWall's dots hold a fixed position/color and flicker independently
// on binary on/off timers, locked to yellow tones. NeonGrid's dots move
// (per-dot depth bob, per-column bar height) and its color washes across
// the whole grid from the album's vivid palette instead of staying fixed
// per dot.

const GRID_SPACING = 0.55;
const COVERAGE = 0.96; // fraction of the visible frustum to fill
const CAMERA_DISTANCE = 7.5;
const GRID_CENTER_Y = 2.0;
const POINT_WORLD_SIZE = 0.22;
const BASE_DIM = 0.035;
const DEPTH_AMOUNT = 0.35; // world units of per-dot forward/back bob - the "depth" in the brief
const COLOR_CYCLE_SPEED = 1 / 28; // full pass through the album's vivid palette every ~28s
const FALLBACK_COLORS = [0xff2fb0, 0x2fd8ff, 0xffb02f, 0x8f2fff, 0x2fff8f];
const BEAT_PULSE_AMOUNT = 0.4;
const BEAT_PULSE_DECAY = 8;


const vertexShader = `
  uniform float uTime;
  uniform float uRows;
  uniform float uSizeScale;
  uniform float uIntensity;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  attribute float aCol;
  attribute float aRow;
  varying vec3 vColor;
  varying float vBrightness;

  void main() {
    // Traveling multi-frequency wave per column -> a bar height in row
    // units, the "digital equalizer" read. Three octaves at different
    // speeds and per-column phase so it never looks like one repeating
    // ripple.
    float w = 0.0;
    w += 0.30 * sin(uTime * 0.9 + aCol * 0.35);
    w += 0.20 * sin(uTime * 1.7 - aCol * 0.6);
    w += 0.14 * sin(uTime * 0.37 + aCol * 0.12);
    float barHeight = clamp(0.5 + w, 0.04, 0.96) * (uRows - 1.0);

    float distBelow = barHeight - aRow;
    float filled = smoothstep(-0.5, 0.15, distBelow); // soft-edged fill under the bar height
    float peak = exp(-pow(distBelow / 0.6, 2.0)); // bright cap right at the bar's top, like a VU meter peak
    float brightness = filled * 0.5 + peak * 0.85;

    // Secondary diagonal shimmer layered on top, so the grid doesn't read
    // as a mechanically flat EQ display - "shifting... dynamic patterns".
    float shimmer = 0.75 + 0.25 * sin(uTime * 1.3 + (aCol + aRow) * 0.25);
    brightness *= shimmer;

    vBrightness = brightness * uIntensity;

    // Color travels across columns via a per-column phase on the same
    // colorA<->colorB blend the JS side slowly rotates through the full
    // album vivid palette - same safe (no dynamic uniform-array indexing)
    // technique storm-layer.js already uses.
    float mixT = 0.5 + 0.5 * sin(uTime * 0.4 + aCol * 0.25);
    vColor = mix(uColorA, uColorB, mixT);

    // Per-dot forward/back bob synced to the same wave - genuine parallax
    // depth rather than a flat brightness-only change.
    vec3 pos = position;
    pos.z += sin(uTime * 1.1 + aCol * 0.3 + aRow * 0.2) * ${DEPTH_AMOUNT.toFixed(3)};

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    float sizeBoost = 0.6 + brightness * 0.9; // dots visibly grow, not just brighten, as they pulse
    gl_PointSize = ${POINT_WORLD_SIZE.toFixed(3)} * sizeBoost * uSizeScale / -mvPosition.z;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const fragmentShader = `
  uniform float uBaseDim;
  varying vec3 vColor;
  varying float vBrightness;

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    if (d > 1.0) discard;
    float edge = pow(1.0 - d, 1.6);

    float brightness = uBaseDim + vBrightness;
    gl_FragColor = vec4(vColor * brightness, edge * (uBaseDim * 0.5 + vBrightness));
  }
`;

export class NeonGridVisualizer extends Visualizer {
  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(0, GRID_CENTER_Y + 0.4, CAMERA_DISTANCE);
    camera.lookAt(0, GRID_CENTER_Y, 0);

    // Visible frustum size at the grid's depth, so it always spans the
    // actual screen edge to edge regardless of aspect ratio/fov - same
    // technique as SpotWall.
    const vFovRad = THREE.MathUtils.degToRad(camera.fov);
    const halfHeight = CAMERA_DISTANCE * Math.tan(vFovRad / 2);
    const halfWidth = halfHeight * camera.aspect;
    const visibleWidth = halfWidth * 2 * COVERAGE;
    const visibleHeight = halfHeight * 2 * COVERAGE;
    const cols = Math.max(2, Math.round(visibleWidth / GRID_SPACING) + 1);
    const rows = Math.max(2, Math.round(visibleHeight / GRID_SPACING) + 1);
    const gridWidth = (cols - 1) * GRID_SPACING;
    const gridHeight = (rows - 1) * GRID_SPACING;
    this._rows = rows;

    const count = cols * rows;
    const positions = new Float32Array(count * 3);
    const aCol = new Float32Array(count);
    const aRow = new Float32Array(count);

    let i = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        positions[i * 3] = col * GRID_SPACING - gridWidth / 2;
        positions[i * 3 + 1] = row * GRID_SPACING - gridHeight / 2 + GRID_CENTER_Y;
        positions[i * 3 + 2] = 0;
        aCol[i] = col;
        aRow[i] = row;
        i++;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aCol', new THREE.BufferAttribute(aCol, 1));
    geometry.setAttribute('aRow', new THREE.BufferAttribute(aRow, 1));

    this._material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uRows: { value: rows },
        uSizeScale: { value: 1 },
        uIntensity: { value: 1 },
        uBaseDim: { value: BASE_DIM },
        uColorA: { value: new THREE.Color(FALLBACK_COLORS[0]) },
        uColorB: { value: new THREE.Color(FALLBACK_COLORS[1]) }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    this._points = new THREE.Points(geometry, this._material);
    scene.add(this._points);

    this._time = 0;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;
    this._material.uniforms.uTime.value = this._time;

    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    this._material.uniforms.uSizeScale.value = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));

    const intensity = profile.director.intensity;
    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    this._material.uniforms.uIntensity.value = (0.7 + intensity * 0.55) * pulse;

    // Same rotate-through-the-whole-palette technique as storm-layer.js:
    // colorA/colorB are the two currently-active stops, stepped forward
    // through every detected vivid color over time rather than only ever
    // blending the first two.
    const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
    const colors = vivid && vivid.length >= 2 ? vivid : FALLBACK_COLORS;
    const n = colors.length;
    const raw = this._time * COLOR_CYCLE_SPEED * n;
    const i0 = Math.floor(raw) % n;
    const i1 = (i0 + 1) % n;
    this._material.uniforms.uColorA.value.set(colors[i0]);
    this._material.uniforms.uColorB.value.set(colors[i1]);
  }

  dispose() {
    this._scene.remove(this._points);
    this._points.geometry.dispose();
    this._material.dispose();
  }
}
